import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { DateInput } from '@/components/ui/date-input';
import { formatDate } from '@/lib/utils';
import { enrollmentDiscountLabel } from '@/lib/enrollmentDiscountLabel';

const FEE_LINES = [
  { value: 'unterricht', label: 'Unterrichtsgebühr' },
  { value: 'gema', label: 'GEMA/Lizenz' },
  { value: 'zusatz', label: 'Zusatz' },
];

const ARTEN = [
  { value: 'Familienpass', fee_target: 'unterricht' },
  { value: 'Gutschein BuT', fee_target: 'unterricht' },
  { value: 'Gebühr', fee_target: 'unterricht' },
  { value: 'Sonderrabatt', fee_target: 'unterricht' },
  { value: 'GEMA-Zuschlag', fee_target: 'gema' },
];

const feeLineLabel = (value) => FEE_LINES.find((row) => row.value === value)?.label || 'Unterrichtsgebühr';

function artLabel(name) {
  return (name || '').trim().toUpperCase() === 'BOT' ? 'Gutschein BuT' : (name || '');
}

const EMPTY = {
  name: 'Familienpass',
  value: '',
  value_type: 'fixed',
  adjustment: 'abzug',
  fee_target: 'unterricht',
  valid_from: '',
  valid_until: '',
  id_number: '',
  issuer: '',
  reference: '',
  renewal_due: '',
  renewal_received: '',
  enrollment_id: '',
  notes: '',
  is_active: true,
};

function betragText(row) {
  const n = Number(row.value);
  if (!Number.isFinite(n)) return '—';
  const mag = Math.abs(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const unit = row.value_type === 'percentage' ? ' %' : ' €';
  const sign = row.adjustment === 'zuschlag' && n >= 0 ? '+' : '−';
  return `${sign} ${mag}${unit}`;
}

function formValue(row) {
  if (row.value == null || row.value === '') return '';
  const n = Number(row.value);
  if (!Number.isFinite(n)) return String(row.value);
  const mag = String(Math.abs(n)).replace('.', ',');
  return row.adjustment === 'zuschlag' && n >= 0 ? `+${mag}` : `-${mag}`;
}

function parseSigned(text) {
  const raw = String(text).trim().replace(/\s/g, '').replace('−', '-').replace(',', '.');
  if (!raw || raw === '+' || raw === '-') return raw ? NaN : 0;
  return Number(raw);
}

function toForm(row) {
  return {
    name: artLabel(row.name) || 'Familienpass',
    value: formValue(row),
    value_type: row.value_type || 'fixed',
    adjustment: row.adjustment === 'zuschlag' ? 'zuschlag' : 'abzug',
    fee_target: row.fee_target || 'unterricht',
    valid_from: row.valid_from || '',
    valid_until: row.valid_until || '',
    id_number: row.id_number || '',
    issuer: row.issuer || '',
    reference: row.reference || '',
    renewal_due: row.renewal_due || '',
    renewal_received: row.renewal_received || '',
    enrollment_id: row.enrollment_id || '',
    notes: row.notes || '',
    is_active: row.is_active !== false,
  };
}

/**
 * Nachweis-Rabatte (Bildungsgutschein etc.) auf der Schülerseite — analog iMikel „Rabatte“.
 */
export default function StudentDiscountsPanel({ studentId, enrollments = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [proofOpen, setProofOpen] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/student-discounts', {
        params: { student_id: studentId },
      });
      setRows(data || []);
    } catch {
      toast.error('Rabatte konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY });
    setProofOpen(false);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm(toForm(row));
    setProofOpen(Boolean(row.id_number || row.issuer || row.reference || row.renewal_due || row.renewal_received));
    setOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error('Art erforderlich');
      return;
    }
    if (!form.valid_from) {
      toast.error('Gültig von erforderlich');
      return;
    }
    const valueNum = parseSigned(form.value);
    if (Number.isNaN(valueNum)) {
      toast.error('Wert ungültig');
      return;
    }
    const payload = {
      student_id: studentId,
      name: form.name.trim(),
      value: valueNum,
      value_type: form.value_type || 'fixed',
      adjustment: valueNum > 0 ? 'zuschlag' : 'abzug',
      fee_target: form.fee_target || 'unterricht',
      valid_from: form.valid_from,
      valid_until: form.valid_until || null,
      id_number: form.id_number || null,
      issuer: form.issuer || null,
      reference: form.reference || null,
      renewal_due: form.renewal_due || null,
      renewal_received: form.renewal_received || null,
      enrollment_id: form.enrollment_id || null,
      notes: form.notes || null,
      is_active: !!form.is_active,
    };
    setSaving(true);
    try {
      if (editId) {
        const { student_id: _s, ...upd } = payload;
        await api.put(`/student-discounts/${editId}`, upd);
        toast.success('Rabatt aktualisiert');
      } else {
        await api.post('/student-discounts', payload);
        toast.success('Rabatt angelegt');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`„${artLabel(row.name)}“ wirklich löschen?`)) return;
    try {
      await api.delete(`/student-discounts/${row.id}`);
      toast.success('Gelöscht');
      load();
    } catch {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="student-discounts">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Rabatte & Zuschläge</h2>
          <p className="text-sm text-slate-500 mt-1">
            Minus ist ein Rabatt, Plus ein Zuschlag. Art und Gebührenzeile bleiben daneben. Das Ende kann offen bleiben.
          </p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="add-student-discount">
          <Plus className="w-4 h-4 mr-1" />
          Eintrag
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center">Keine Nachweise hinterlegt</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 font-semibold">von</th>
                <th className="px-3 py-2 font-semibold">bis</th>
                <th className="px-3 py-2 font-semibold">Bezeichnung</th>
                <th className="px-3 py-2 font-semibold text-right">Wert</th>
                <th className="px-3 py-2 font-semibold">Ausweisnummer</th>
                <th className="px-3 py-2 font-semibold">Neuantrag Eingang</th>
                <th className="px-3 py-2 font-semibold w-24" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.id} className={!r.is_active ? 'opacity-50' : ''}>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.valid_from)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.valid_until ? formatDate(r.valid_until) : 'unbegrenzt'}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{artLabel(r.name)}</div>
                    <div className="text-xs text-slate-500">{feeLineLabel(r.fee_target)}</div>
                    {(r.issuer || r.reference) && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        {[r.issuer, r.reference].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {r.notes ? (
                      <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap" data-testid={`discount-row-notes-${r.id}`}>
                        {r.notes}
                      </div>
                    ) : null}
                    {r.enrollment_label && (
                      <Badge variant="outline" className="mt-1 text-xs font-normal">
                        {enrollmentDiscountLabel({ course_name: r.enrollment_label }) || r.enrollment_label}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {betragText(r)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.id_number || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.renewal_received ? formatDate(r.renewal_received) : '—'}
                    {r.renewal_due && (
                      <div className="text-xs text-amber-700">fällig {formatDate(r.renewal_due)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => remove(r)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Eintrag bearbeiten' : 'Eintrag anlegen'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label>Art</Label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                value={form.name}
                onChange={(e) => {
                  const art = ARTEN.find((row) => row.value === e.target.value);
                  setForm((f) => ({
                    ...f,
                    name: e.target.value,
                    fee_target: art ? art.fee_target : f.fee_target,
                  }));
                }}
                data-testid="discount-name"
              >
                {(ARTEN.some((row) => row.value === form.name) ? ARTEN : [{ value: form.name }, ...ARTEN]).map((row) => (
                  <option key={row.value} value={row.value}>{row.value}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Gebührenzeile</Label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                value={form.fee_target || 'unterricht'}
                onChange={(e) => set('fee_target', e.target.value)}
                data-testid="discount-fee-target"
              >
                {FEE_LINES.map((row) => (
                  <option key={row.value} value={row.value}>{row.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Ein negativer Betrag mindert nur diese Zeile, bis sie 0,00 € ist. Ein positiver Betrag kommt dazu.
              </p>
            </div>
            <div>
              <Label>Betrag oder Prozent</Label>
              <Input
                value={form.value}
                onChange={(e) => set('value', e.target.value)}
                placeholder="−10,00 oder +10,00"
                data-testid="discount-value"
              />
              <p className="mt-1 text-xs text-slate-500">Minus ist ein Rabatt, Plus ein Zuschlag.</p>
            </div>
            <div>
              <Label className="invisible">Art des Betrags</Label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                value={form.value_type || 'fixed'}
                onChange={(e) => set('value_type', e.target.value)}
                data-testid="discount-value-type"
              >
                <option value="fixed">Betrag €</option>
                <option value="percentage">Prozent</option>
              </select>
            </div>
            <div>
              <Label>Beginn</Label>
              <DateInput
                value={form.valid_from}
                onChange={(e) => set('valid_from', e.target.value)}
                data-testid="discount-valid-from"
              />
            </div>
            <div>
              <Label>Ende (optional)</Label>
              <DateInput
                value={form.valid_until}
                onChange={(e) => set('valid_until', e.target.value)}
                data-testid="discount-valid-until"
              />
              <p className="mt-1 text-xs text-slate-500">Leer lassen heißt unbegrenzt.</p>
            </div>
            <div className="col-span-2">
              <Label>Belegung (optional)</Label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                value={form.enrollment_id || ''}
                onChange={(e) => set('enrollment_id', e.target.value)}
                data-testid="discount-enrollment"
              >
                <option value="">Alle Belegungen (1×)</option>
                {(enrollments || []).map((en) => (
                  <option key={en.id} value={en.id}>
                    {enrollmentDiscountLabel(en) || 'Belegung'}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Ausweisnummer</Label>
              <Input
                value={form.id_number}
                onChange={(e) => set('id_number', e.target.value)}
                placeholder="101897528728"
                data-testid="discount-id-number"
              />
            </div>
            {proofOpen && (
              <>
                <div className="col-span-2">
                  <Label>Aussteller</Label>
                  <Input value={form.issuer} onChange={(e) => set('issuer', e.target.value)} placeholder="Jobcenter Kreis Steinfurt" />
                </div>
                <div>
                  <Label>Aktenzeichen</Label>
                  <Input value={form.reference} onChange={(e) => set('reference', e.target.value)} placeholder="TA.100950" />
                </div>
                <div>
                  <Label>Neuantrag fällig</Label>
                  <Input type="date" value={form.renewal_due} onChange={(e) => set('renewal_due', e.target.value)} />
                </div>
                <div>
                  <Label>Neuantrag Eingang</Label>
                  <Input type="date" value={form.renewal_received} onChange={(e) => set('renewal_received', e.target.value)} />
                </div>
              </>
            )}
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={proofOpen}
                  onChange={(e) => setProofOpen(e.target.checked)}
                  data-testid="discount-proof-toggle"
                />
                Nachweis ergänzen
              </label>
            </div>
            <div className="col-span-2">
              <Label>Notiz</Label>
              <Input
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                data-testid="discount-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="save-student-discount">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
